import { DialogWithForm } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { post } from 'services/Http';
import { mapSuppliersToOptions } from 'services/NPISupport';

export const SupplierConsolidationDialog = ({ isOpen, suppliers, onClose, onConsolidated }) => {
    const parentSuppliers = suppliers.filter((supplier) => supplier.parent_supplier_npi == null);
    const sourceOptions = mapSuppliersToOptions(
        parentSuppliers
            .map((supplier) => ({
                ...supplier,
                children: suppliers.filter(
                    (child) => child.parent_supplier_npi === supplier.supplier_npi,
                ),
            }))
            .filter((supplier) => supplier.children.length > 0),
    );
    const targetOptions = mapSuppliersToOptions(parentSuppliers);

    return (
        <Formik
            initialValues={{ sourceSupplier: undefined, targetSupplier: undefined }}
            validationSchema={Yup.object({
                sourceSupplier: Yup.object().required(validationStrings.requiredField.errorMessage),
                targetSupplier: Yup.object().required(validationStrings.requiredField.errorMessage),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await post('/reference/suppliers/migrate_children', {
                        children_to_migrate: values.sourceSupplier.children.map(
                            (child) => child.supplier_npi,
                        ),
                        new_parent_npi: values.targetSupplier.supplier_npi,
                    });
                    SuccessToast({ message: 'Suppliers consolidated successfully.' });
                    await onConsolidated();
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Error consolidating suppliers.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Consolidate Suppliers"
                    width={600}
                    isOpen={isOpen}
                    primaryButtonLabel="Consolidate Suppliers"
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        formik.resetForm();
                        onClose();
                    }}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    dialogContent={
                        <>
                            <div className="margin__bottom--medium">
                                <InlineMessage
                                    label="All supplier locations will move from the source supplier to the target supplier. If no locations remain, the source supplier will be removed."
                                    intent={InlineMessage.Intent.WARNING}
                                    wrapText
                                />
                            </div>
                            <SuggestSingleSelectFormik
                                id="sourceSupplier"
                                label="Source Supplier"
                                required
                                values={sourceOptions}
                                onChange={(sourceSupplier) => {
                                    formik.setFieldValue('sourceSupplier', sourceSupplier);
                                    if (
                                        sourceSupplier?.supplier_npi ===
                                        formik.values.targetSupplier?.supplier_npi
                                    ) {
                                        formik.setFieldValue('targetSupplier', undefined);
                                    }
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="targetSupplier"
                                label="Target Supplier"
                                required
                                disabled={!formik.values.sourceSupplier}
                                values={targetOptions.filter(
                                    (supplier) =>
                                        supplier.supplier_npi !==
                                        formik.values.sourceSupplier?.supplier_npi,
                                )}
                            />
                            {formik.values.sourceSupplier && (
                                <>
                                    <p className="margin__top--medium margin__bottom--small">
                                        Supplier locations to move:
                                    </p>
                                    <ul>
                                        {formik.values.sourceSupplier.children.map((child) => (
                                            <li key={child.supplier_npi}>
                                                {child.organization_name} ({child.supplier_npi})
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

SupplierConsolidationDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    suppliers: PropTypes.arrayOf(
        PropTypes.shape({
            supplier_npi: PropTypes.string.isRequired,
            parent_supplier_npi: PropTypes.string,
            organization_name: PropTypes.string.isRequired,
            network_status: PropTypes.string,
        }),
    ).isRequired,
    onClose: PropTypes.func.isRequired,
    onConsolidated: PropTypes.func.isRequired,
};
